<template>
  <div class="help-page fade-in">
    <!-- HEADER -->
    <div class="help-header">
      <h2><i class="bx bx-help-circle"></i> Complete Your Setup</h2>
      <p>Finish these quick steps to activate your meeting system.</p>
    </div>


    <!-- PROGRESS BAR -->
    <div class="progress-container">
      <div class="progress-bar" :style="{ width: progress + '%' }"></div>
    </div>
    <p class="progress-text">
      {{ completedSteps }}/{{ steps.length }} Completed
    </p>

    <!-- SETUP STEPS -->
    <transition-group name="card-fade" tag="div" class="steps-container">
      <div v-for="(step, index) in steps" :key="step.title" class="step-card" :class="{ completed: step.completed }">
        <div class="pulse-glow"></div>
        <div class="step-icon motion">
          <i :class="step.icon"></i>
        </div>

        <div class="step-content">
          <h3>{{ step.title }}</h3>
          <p>{{ step.description }}</p>
        </div>

        <div class="step-action">
          <button class="btn-primary" :class="{ done: step.completed }" @click="toggleComplete(index)">
            <i :class="step.completed ? 'bx bx-check-circle' : 'bx bx-play-circle'"></i>
            {{ step.completed ? 'Completed' : step.button }}
          </button>
        </div>
      </div>
    </transition-group>

    <!-- FOOTER -->
    <div class="help-footer">
      <button class="btn-black" @click="finishSetup">
        <i class="bx bx-rocket"></i> Launch Dashboard
      </button>
      <p v-if="completedSteps < steps.length">
        Complete all steps to unlock full access.
      </p>
    </div>
  </div>
</template>

<script setup>
  import { ref, computed, onMounted } from "vue";

  import {
    checkIntegration
  } from "@/apis/adminapis";
  import { getBookPages } from "@/apis/bookpageapis";
  import { getProfiles, } from "@/apis/profileapi";

  import {
    getPackages
  } from "@/apis/pkgapis";

  const steps = ref([
    {
      title: "Connect Your Calendar",
      description: "Sync Google Calendar or Outlook to manage your meetings automatically.",
      icon: "bx bx-calendar",
      button: "Connect Calendar",
      link: "/settings/calendar",  // redirect path
      completed: false,
    },
    {
      title: "Connect Payment Gateway",
      description: "Integrate Stripe, PayPal to accept online payments.",
      icon: "bx bx-credit-card",
      button: "Connect Gateway",
      link: "/settings/payments", // redirect path
      completed: false,
    },
    {
      title: "Create Your Booking Page",
      description: "Set up your meeting types and availability for clients to book.",
      icon: "bx bx-calendar-plus",
      button: "Create Booking Page",
      link: "/booking/create", // redirect path
      completed: false,
    },
    {
      title: "Create Payment Plans",
      description: "Add pricing plans that clients can select when booking services.",
      icon: "bx bx-collection",
      button: "Create Plans",
      link: "/pricing/setup", // redirect path
      completed: false,
    },
    {
      title: "Create Your Page",
      description: "Set up your business page with branding.",
      icon: "bx bx-globe",
      button: "Create Page",
      link: "open-website-popup", // special popup event
      completed: false,
    },
  ]);

  onMounted(async () => {
    const ldataRaw = sessionStorage.getItem("ldata");
    const ldata = ldataRaw ? JSON.parse(ldataRaw) : null;

    var userId = ldata.user_id;

    const google = await checkIntegration(userId, "Google Calendar", "google_calendar");
    const paypal = await checkIntegration(userId, "PayPal", "payment");
    const stripe = await checkIntegration(userId, "Stripe", "payment");
    const razorpay = await checkIntegration(userId, "Razorpay", "payment");
    const gmail = await checkIntegration(userId, "Gmail", "email");

    // 🔹 Auto mark steps as completed
    if (google?.status) steps.value[0].completed = true;
    if (paypal?.status || stripe?.status || razorpay?.status)
      steps.value[1].completed = true;
    if (gmail?.status) steps.value[2].completed = true;


    const res = await getBookPages(
      userId,
      "0",
      "",
      "",
      ""
    );
    if (res.status && res.records?.length) {
      steps.value[2].completed = true;
    }

    const res1 = await getPackages(
      userId,
      "0",
      "",
      ""
    );
    if (res1?.status && res1?.records?.length > 0) {
      steps.value[3].completed = true;
    }

     const res2 = await getProfiles(userId, "0", "");
    if (res2?.status) {
      steps.value[4].completed = true;
    }

  });

  const completedSteps = computed(() => steps.value.filter(s => s.completed).length);
  const progress = computed(() => (completedSteps.value / steps.value.length) * 100);


  const toggleComplete = (index) => {
    var pname = "booking_pages";
    switch(index){
      case 0:
        pname = "settings?rtype=calender";
      break;
      case 1:
        pname = "settings?rtype=payment";
      break;
     
      case 2:
        pname = "add_booking_page";
      break;
      case 3:
        pname = "new_package";
      break;
      case 4:
        pname = "my_pages";
      break;
    }

    window.location.href = "/"+pname;
  };

  const finishSetup = () => {
    window.location.href = "/";
  };
</script>

<style scoped>
  .help-page {
    background: #fff;
    border-radius: 1.5rem;
    padding: 2rem 2.5rem;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    font-family: "Inter", sans-serif;
    max-width: 850px;
    margin: 3rem auto;
    position: relative;
    overflow: hidden;
  }

  /* HEADER */
  .help-header {
    text-align: center;
    margin-bottom: 2rem;
    position: relative;
    z-index: 2;
  }

  .help-header h2 {
    font-size: 1.9rem;
    font-weight: 700;
    color: #111827;
  }

  .help-header p {
    color: #6b7280;
    margin-top: 0.4rem;
  }

  /* FLOATING DOODLES */
  .floating-doodles {
    position: absolute;
    inset: 0;
    overflow: hidden;
    z-index: 0;
  }

  .doodle {
    position: absolute;
    font-size: 2.5rem;
    opacity: 0.06;
    color: #111827;
  }

  .doodle:nth-child(1) {
    top: 10%;
    left: 15%;
  }

  .doodle:nth-child(2) {
    top: 70%;
    left: 25%;
  }

  .doodle:nth-child(3) {
    top: 40%;
    right: 10%;
  }

  .doodle:nth-child(4) {
    bottom: 10%;
    right: 35%;
  }

  @keyframes float {

    0%,
    100% {
      transform: translateY(0) rotate(0deg);
    }

    50% {
      transform: translateY(-20px) rotate(8deg);
    }
  }

  /* PROGRESS BAR */
  .progress-container {
    background: #f3f4f6;
    border-radius: 10px;
    height: 10px;
    overflow: hidden;
    margin-bottom: 0.5rem;
    position: relative;
    z-index: 2;
  }

  .progress-bar {
    background: linear-gradient(90deg, #111827, #1e3a8a);
    height: 100%;
    width: 0;
    border-radius: 10px;
    transition: width 0.6s ease;
  }

  .progress-text {
    text-align: right;
    font-size: 0.9rem;
    color: #6b7280;
    margin-bottom: 2rem;
  }

  /* STEP CARDS */
  .steps-container {
    display: flex;
    flex-direction: column;
    gap: 1.3rem;
    z-index: 2;
    position: relative;
  }

  .step-card {
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1.2rem 1.5rem;
    transition: all 0.4s ease;
    position: relative;
    overflow: hidden;
  }

  .step-card:hover {
    background: #f3f4f6;
    transform: translateY(-4px);
    box-shadow: 0 6px 16px rgba(17, 24, 39, 0.08);
  }

  .step-card.completed {
    background: #dcfce7;
    border-color: #86efac;
  }

  @keyframes pulse {
    from {
      box-shadow: 0 0 0 rgba(22, 163, 74, 0.3);
    }

    to {
      box-shadow: 0 0 15px rgba(22, 163, 74, 0.5);
    }
  }

  /* Icon Motion */
  .step-icon {
    background: #111827;
    color: #fff;
    width: 55px;
    height: 55px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  @keyframes bounce {

    0%,
    100% {
      transform: translateY(0);
    }

    50% {
      transform: translateY(-6px);
    }
  }

  /* STEP CONTENT */
  .step-content {
    flex: 1;
  }

  .step-content h3 {
    color: #111827;
    font-size: 1rem;
    font-weight: 600;
  }

  .step-content p {
    color: #6b7280;
    font-size: 0.9rem;
    margin-top: 0.2rem;
  }

  /* STEP ACTION */
  .btn-primary {
    background: #111827;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 0.6rem 1.2rem;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    transition: all 0.3s ease;
  }

  .btn-primary:hover {
    background: #1f2937;
    transform: translateY(-1px);
  }

  .btn-primary.done {
    background: #16a34a;
  }

  .btn-primary.done:hover {
    background: #15803d;
  }

  /* FOOTER */
  .help-footer {
    text-align: center;
    margin-top: 2rem;
    position: relative;
    z-index: 2;
  }

  .btn-black {
    background: linear-gradient(90deg, #111827, #1e3a8a);
    color: #fff;
    border: none;
    border-radius: 10px;
    padding: 0.9rem 1.6rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  .btn-black:hover {
    transform: scale(1.03);
    box-shadow: 0 6px 14px rgba(17, 24, 39, 0.25);
  }

  .help-footer p {
    color: #6b7280;
    font-size: 0.9rem;
    margin-top: 0.8rem;
  }

  .card-fade-enter-active {
    transition: all 0.5s ease;
  }

  .card-fade-enter-from {
    opacity: 0;
    transform: translateY(20px);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  /* 📱 HELP PAGE RESPONSIVENESS PATCH — DO NOT REMOVE ANYTHING ABOVE */

  /* 🖥️ Tablet Adjustments (≤1024px) */
  @media (max-width: 1024px) {
    .help-page {
      padding: 1.8rem 2rem !important;
      margin: 2rem auto !important;
      max-width: 95% !important;
    }

    .step-card {
      flex-wrap: wrap !important;
      align-items: flex-start !important;
      text-align: left !important;
      gap: 1rem !important;
    }

    .step-icon {
      width: 50px !important;
      height: 50px !important;
      font-size: 1.4rem !important;
    }

    .step-content h3 {
      font-size: 1rem !important;
    }

    .step-content p {
      font-size: 0.9rem !important;
    }

    .btn-primary {
      font-size: 0.9rem !important;
      padding: 0.6rem 1.1rem !important;
    }
  }

  /* 📱 Mobile (≤768px) */
  @media (max-width: 768px) {
    .help-page {
      width: 100% !important;
      padding: 1.5rem !important;
      border-radius: 1rem !important;
    }

    .help-header h2 {
      font-size: 1.5rem !important;
    }

    .help-header p {
      font-size: 0.95rem !important;
    }

    .progress-text {
      text-align: center !important;
      font-size: 0.85rem !important;
    }

    .steps-container {
      gap: 1rem !important;
    }

    .step-card {
      flex-direction: column !important;
      align-items: flex-start !important;
      text-align: left !important;
      padding: 1rem !important;
    }

    .step-icon {
      margin: 0 0 0.5rem 0 !important;
      width: 45px !important;
      height: 45px !important;
      font-size: 1.3rem !important;
    }

    .step-content {
      width: 100% !important;
    }

    .step-content h3 {
      font-size: 1rem !important;
      margin-bottom: 0.25rem !important;
    }

    .step-content p {
      font-size: 0.9rem !important;
      line-height: 1.4 !important;
    }

    .step-action {
      width: 100% !important;
      margin-top: 0.75rem !important;
    }

    .btn-primary {
      width: 100% !important;
      justify-content: center !important;
      font-size: 0.95rem !important;
      padding: 0.8rem 1rem !important;
    }

    .help-footer {
      margin-top: 1.8rem !important;
    }

    .btn-black {
      width: 100% !important;
      justify-content: center !important;
      padding: 0.9rem 1rem !important;
      font-size: 1rem !important;
    }

    .help-footer p {
      font-size: 0.85rem !important;
    }
  }

  /* 📱 Small Mobile (≤480px) */
  @media (max-width: 480px) {
    .help-page {
      padding: 1.2rem !important;
      margin: 1.5rem auto !important;
      border-radius: 0.75rem !important;
    }

    .help-header h2 {
      font-size: 1.3rem !important;
    }

    .help-header p {
      font-size: 0.85rem !important;
    }

    .progress-text {
      font-size: 0.8rem !important;
    }

    .step-card {
      padding: 0.9rem !important;
    }

    .step-content h3 {
      font-size: 0.95rem !important;
    }

    .step-content p {
      font-size: 0.85rem !important;
    }

    .btn-primary,
    .btn-black {
      font-size: 0.85rem !important;
      padding: 0.7rem 1rem !important;
    }

    .step-icon {
      width: 42px !important;
      height: 42px !important;
      font-size: 1.1rem !important;
    }

    .help-footer {
      margin-top: 1.5rem !important;
    }
  }
</style>